import { LobbyImage } from '@colonist/types';
import { createDivIn, createHeaderIn, createImgIn, createSliderIn, createSpanIn, setElementInnerText } from '../../ui_utils/DomUtils.ts';
import { createTooltipIn } from '../../ui_utils/TooltipUtils.ts';
export class UIRoomSliderController {
    get isDisabled() {
        return this._isDisabled;
    }
    set isDisabled(value) {
        this._isDisabled = value;
        this.slider.disabled = value;
        this.setSliderVisibility();
    }
    createContainer() {
        this.sliderContainer = createDivIn(this.container, 'room-settings-slider-container');
    }
    createHeader() {
        this.headerContainer = createDivIn(this.sliderContainer, 'room-settings-slider-header');
        const titleContainer = createDivIn(this.headerContainer, 'room-settings-slider-title');
        createHeaderIn(titleContainer, this.title, '');
        this.sliderWarningImage = createImgIn(titleContainer, LobbyImage.DangerIcon, 'hidden');
        createTooltipIn(this.sliderWarningImage, {
            key: 'strings:roomPage.options.vpToWin.aboveRecommendedLimit'
        });
    }
    createSlider() {
        this.sliderTrackContainer = createDivIn(this.sliderContainer, 'room-settings-slider-track-container');
        createSpanIn(this.sliderTrackContainer, 'room-settings-slider-label', {
            key: 'strings:colonistCommon.utils.#buffer',
            options: {
                value: this.min
            }
        });
        const sliderWrapper = createDivIn(this.sliderTrackContainer, 'room-settings-slider-wrapper');
        this.slider = createSliderIn(sliderWrapper, 'room-settings-slider-input', '', this.min, this.max, this.step, this.defaultValue);
        this.sliderTrack = createDivIn(sliderWrapper, 'room-settings-slider-track');
        this.sliderThumb = createSpanIn(sliderWrapper, 'room-settings-slider-thumb', {
            key: 'strings:colonistCommon.utils.#buffer',
            options: {
                value: this.defaultValue
            }
        });
        createSpanIn(this.sliderTrackContainer, 'room-settings-slider-label', {
            key: 'strings:colonistCommon.utils.#buffer',
            options: {
                value: this.max
            }
        });
        this.textDisplay = createSpanIn(this.sliderContainer, 'room-settings-slider-text-display', {
            key: 'strings:colonistCommon.utils.#buffer',
            options: {
                value: this.defaultValue
            }
        });
        this.slider.oninput = ()=>this.handleOnInput();
        this.slider.onchange = ()=>this.handleOnChange();
        this.resizeObserver = new ResizeObserver(()=>this.updateSlider());
        this.resizeObserver.observe(this.slider);
        this.updateSlider();
    }
    removeSlider() {
        this.resizeObserver.disconnect();
        this.sliderTrackContainer.remove();
        this.textDisplay.remove();
    }
    setSliderVisibility() {
        this.sliderContainer.classList.toggle('view-only', this.isDisabled);
    }
    updateSlider() {
        const currentValue = this.slider.valueAsNumber;
        const percent = (currentValue - this.min) / (this.max - this.min);
        const sliderWidth = this.slider.offsetWidth;
        const minThumbPadding = 4;
        const thumbPosition = percent * sliderWidth;
        const thumbDisplayedPosition = Math.max(minThumbPadding, Math.min(thumbPosition, sliderWidth - minThumbPadding));
        this.sliderThumb.style.left = `${thumbDisplayedPosition}px`;
        setElementInnerText(this.sliderThumb, {
            key: 'strings:colonistCommon.utils.#buffer',
            options: {
                value: currentValue
            }
        });
        const isModified = currentValue != this.defaultValue;
        this.sliderContainer.classList.toggle('modified', isModified);
        this.sliderThumb.classList.toggle('modified', isModified);
        this.sliderTrack.style.setProperty('--progress-percent', `${percent * 100}%`);
        this.sliderTrack.classList.toggle('warning', this.isAboveWarningThreshold());
        this.sliderWarningImage.classList.toggle('hidden', !this.isAboveWarningThreshold());
        setElementInnerText(this.textDisplay, {
            key: 'strings:colonistCommon.utils.#buffer',
            options: {
                value: currentValue
            }
        });
    }
    updateSliderConfig(defaultValue, max) {
        if (defaultValue == this.defaultValue && max == this.max) return;
        this.defaultValue = defaultValue;
        this.max = max;
        this.removeSlider();
        this.createSlider();
    }
    setWarningThreshold(warningThreshold) {
        this.sliderWarningThreshold = warningThreshold;
    }
    isAboveWarningThreshold() {
        return this.slider.valueAsNumber > this.sliderWarningThreshold;
    }
    handleOnInput() {
        if (this.isDisabled) return;
        if (this.onInput != undefined) {
            this.onInput(this.slider.valueAsNumber);
        }
        this.updateSlider();
    }
    handleOnChange() {
        this.container.style.overflow = '';
        if (this.isDisabled) return;
        if (this.onChange) this.onChange(this.slider.valueAsNumber);
    }
    setOnInput(onInput) {
        this.onInput = onInput;
    }
    setValue(value, isDisabled = false) {
        this.slider.value = value.toString();
        this.isDisabled = isDisabled;
        this.updateSlider();
    }
    constructor(container, title, defaultValue, min, max, step = 1, onChange, isDisabled = false){
        this.container = container;
        this.title = title;
        this.defaultValue = defaultValue;
        this.min = min;
        this.max = max;
        this.step = step;
        this.onChange = onChange;
        this.createContainer();
        this.createHeader();
        this.createSlider();
        this.isDisabled = isDisabled;
    }
}
